import { Component, ViewEncapsulation, OnInit, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { NgClass } from '@angular/common';
import { RouterLink } from '@angular/router';
import { Subject, takeUntil, lastValueFrom } from 'rxjs';
import { StrapiService } from '../dealers/strapi.service';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { slugify } from '../../../core/utils/slugify';
import { GtagService } from '../../../core/services/gtag.service';
import { MetaPixelService } from '../../../core/services/meta-pixel.service';

interface CarouselImage {
    id: number;
    src: string;
    alt: string;
    title: string;
    subtitle: string;
}

interface Vehicle {
    id: string;
    documentId: string;
    name: string;
    hasSubVehicles: boolean;
}

@Component({
    selector: 'landing-footer',
    templateUrl: './footer.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [MatButtonModule, MatIconModule, FormsModule, NgClass, RouterLink],
    host: { 'class': 'block w-full max-w-full' },
    styles: [
        `:host{display:block;width:100%;max-width:100vw;overflow-x:hidden}`
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class LandingFooterComponent implements OnInit, OnDestroy {
    currentYear = new Date().getFullYear();
    currentSlide = 0;
    totalSlides = 5; // 5 positions (0-4) to show all 8 images in groups of 4

    // 360 Degree Viewer Properties
    currentFrame = 0;
    totalFrames = 36; // 36 images for a full 360° rotation (10° increments)
    isLoading = false;
    autoRotate = false;
    isDragging = false;
    lastX = 0;
    autoRotateInterval: any;

    // Vehicle data from Strapi
    vehicles: Vehicle[] = [];
    isLoadingVehicles = true;
    slugify = slugify;
    isScreenSmall = false;

    // License section data
    licenseSection: any = null;
    isLoadingLicenses = true;

    // Country flag data
    countryFlagImage: any = null;

    private _unsubscribeAll = new Subject<void>();
    // 360 Degree Images - Placeholder URLs (you'll replace these with actual 360° images)
    private generate360Images(): string[] {
        const images: string[] = [];
        for (let i = 1; i <= this.totalFrames; i++) {
            const num = i.toString().padStart(2, '0');
            images.push(`https://res.cloudinary.com/demo/image/upload/car_360/car-${num}.jpg`);
        }
        return images;
    }

    private _360Images: string[] = this.generate360Images();

    get current360Image(): string {
        return this._360Images[this.currentFrame];
    }

    // Carousel images data - will eventually be pulled from Strapi
    carouselImages: CarouselImage[] = [
        {
            id: 1,
            src: 'https://images.unsplash.com/photo-1503376780353-7e6692767b70?auto=format&fit=crop&w=800&q=80',
            alt: 'Classic Car 1',
            title: 'Classic Beauty',
            subtitle: 'Timeless Design'
        },
        {
            id: 2,
            src: 'https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?auto=format&fit=crop&w=800&q=80',
            alt: 'Classic Car 2',
            title: 'Muscle Power',
            subtitle: 'Raw Performance'
        },
        {
            id: 3,
            src: 'https://images.unsplash.com/photo-1552519507-da3b142c6e3d?auto=format&fit=crop&w=800&q=80',
            alt: 'Classic Car 3',
            title: 'Vintage Charm',
            subtitle: 'Heritage Collection'
        },
        {
            id: 4,
            src: 'https://images.unsplash.com/photo-1580274455191-1c62238fa333?auto=format&fit=crop&w=800&q=80',
            alt: 'Classic Car 4',
            title: 'Legendary Status',
            subtitle: 'Iconic Design'
        },
        {
            id: 5,
            src: 'https://images.unsplash.com/photo-1503376780353-7e6692767b70?auto=format&fit=crop&w=800&q=80',
            alt: 'Classic Car 5',
            title: 'Classic Beauty',
            subtitle: 'Timeless Design'
        },
        {
            id: 6,
            src: 'https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?auto=format&fit=crop&w=800&q=80',
            alt: 'Classic Car 6',
            title: 'Muscle Power',
            subtitle: 'Raw Performance'
        },
        {
            id: 7,
            src: 'https://images.unsplash.com/photo-1552519507-da3b142c6e3d?auto=format&fit=crop&w=800&q=80',
            alt: 'Classic Car 7',
            title: 'Vintage Charm',
            subtitle: 'Heritage Collection'
        },
        {
            id: 8,
            src: 'https://images.unsplash.com/photo-1580274455191-1c62238fa333?auto=format&fit=crop&w=800&q=80',
            alt: 'Classic Car 8',
            title: 'Legendary Status',
            subtitle: 'Iconic Design'
        }
    ];

    /**
     * Constructor
     */
    constructor(
        private router: Router,
        private _strapiService: StrapiService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _gtagService: GtagService,
        private _metaPixelService: MetaPixelService
    ) { }

    /**
     * Initialize component
     */
    async ngOnInit(): Promise<void> {
        // Media watcher setup
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ matchingAliases }) => {
                this.isScreenSmall = !matchingAliases.includes('md');
                this._changeDetectorRef.markForCheck();
            });

        // Load critical data first (vehicles, license section, country flag) in parallel
        // Defer 360 image loading to avoid blocking footer rendering
        this.load360Images();

        // Load footer content in parallel for better performance
        await Promise.all([
            this.loadVehicles(),
            this.loadLicenseSection(),
            this.loadCountryFlag()
        ]).catch(error => console.error('Error loading footer data:', error));
    }

    /**
     * Cleanup on destroy
     */
    ngOnDestroy(): void {
        this.stopAutoRotate();
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    /**
     * Load vehicles from Strapi
     */
    async loadVehicles(): Promise<void> {
        try {
            this.isLoadingVehicles = true;
            const res: any = await lastValueFrom(this._strapiService.getVehicleModels());
            this.vehicles = res.data?.map((vehicle: any) => ({
                id: vehicle.id,
                documentId: vehicle.documentId,
                name: vehicle.name,
                hasSubVehicles: vehicle.hasSubVehicles || false
            })) || [];
            this._changeDetectorRef.markForCheck();
        } catch (error) {
            console.error('Error loading vehicles:', error);
            // Fallback to empty array
            this.vehicles = [];
        } finally {
            this.isLoadingVehicles = false;
        }
    }

    /**
     * Load license section from Strapi
     */
    async loadLicenseSection(): Promise<void> {
        try {
            this.isLoadingLicenses = true;
            const res: any = await lastValueFrom(this._strapiService.getHomeVideoLicenseSection());
            const data = res.data;

            // Precompute full image URLs to prevent recalculation on scroll
            if (data) {
                this.licenseSection = {
                    ...data,
                    images: data.images?.map((img: any) => ({
                        ...img,
                        fullUrl: this._strapiService.getFullImageUrl(img.url) || null
                    })) || []
                };
            } else {
                this.licenseSection = null;
            }

            this._changeDetectorRef.markForCheck();
        } catch (error) {
            console.error('Error loading license section:', error);
            this.licenseSection = null;
        } finally {
            this.isLoadingLicenses = false;
        }
    }

    /**
     * Load country flag from Strapi
     */
    async loadCountryFlag(): Promise<void> {
        try {
            const res: any = await lastValueFrom(this._strapiService.getHomeVideoMain());
            const flagData = res.data?.countryFlagImage;

            // Precompute full image URL to prevent recalculation on scroll
            if (flagData) {
                this.countryFlagImage = {
                    ...flagData,
                    fullUrl: this._strapiService.getFullImageUrl(flagData.url) || null
                };
            } else {
                this.countryFlagImage = null;
            }

            this._changeDetectorRef.markForCheck();
        } catch (error) {
            console.error('Error loading country flag:', error);
            this.countryFlagImage = null;
        }
    }

    /**
     * Navigate to vehicle page based on hasSubVehicles property
     */
    navigateToVehicle(vehicle: Vehicle): void {
        if (vehicle.hasSubVehicles) {
            // Navigate to vehicle listing page if it has subvehicles
            this.router.navigate(['/vehicles', slugify(vehicle.name)], { state: { id: vehicle.id } });
        } else {
            // Navigate to vehicle detail page if it doesn't have subvehicles
            this.router.navigate(['/vehicles', slugify(vehicle.name), 'detail'], { state: { id: vehicle.id } });
        }
    }

    /**
     * Company section navigation methods
     */
    navigateToAbout(): void {
        this.router.navigate(['/about']);
    }

    navigateToLicense(): void {
        this.router.navigate(['/license']);
    }

    navigateToInventory(): void {
        this.router.navigate(['/inventory']);
    }

    navigateToGallery(): void {
        this.router.navigate(['/gallery']);
    }

    navigateToVehicles(): void {
        this.router.navigate(['/vehicles']);
    }

    navigateToFAQ(): void {
        this.router.navigate(['/faq']);
    }

    /**
     * Support section navigation methods
     */
    navigateToDealers(): void {
        this.router.navigate(['/dealers']);
    }

    navigateToContact(): void {
        this.router.navigate(['/contact']);
    }

    /**
     * Track click on footer dealer hotline (+1800 29 SNAKE)
     */
    onDealerHotlineClick(): void {
        this._gtagService.event('dealer_hotline_click', { location: 'footer', label: '+1800 29 SNAKE (Dealer Hotline)' });
        this._metaPixelService.trackCustom('DealerHotlineClick', { location: 'footer', label: '+1800 29 SNAKE (Dealer Hotline)' });
    }

    /**
     * Track click on the footer email address (info@superformance.com)
     */
    onFooterEmailClick(): void {
        this._gtagService.event('footer_email_click', { location: 'footer', email: 'info@superformance.com' });
        this._metaPixelService.trackCustom('FooterEmailClick', { location: 'footer', email: 'info@superformance.com' });
    }

    navigateToPrivacyPolicy(): void {
        this.router.navigate(['/privacy-policy']);
    }

    /**
     * Load 360 degree images
     */
    private load360Images(): void {
        this.isLoading = true;
        // Simulate loading time
        setTimeout(() => {
            this.isLoading = false;
        }, 1000);
    }

    /**
     * Handle form submission
     */
    onSubmit(): void {
        // Handle form submission logic here
        // Contact form submitted - handle API call or email service here
        // You can add your API call or email service here
    }

    /**
     * Go to next slide
     */
    nextSlide(): void {
        if (this.currentSlide < this.totalSlides - 1) {
            this.currentSlide++;
        }
    }

    /**
     * Go to previous slide
     */
    previousSlide(): void {
        if (this.currentSlide > 0) {
            this.currentSlide--;
        }
    }

    /**
     * Go to specific slide
     */
    goToSlide(index: number): void {
        this.currentSlide = index;
    }



    // 360 Degree Viewer Methods

    /**
     * Start drag interaction
     */
    startDrag(event: MouseEvent): void {
        this.isDragging = true;
        this.lastX = event.clientX;
        this.stopAutoRotate();
        event.preventDefault();
    }

    /**
     * Handle drag movement
     */
    onDrag(event: MouseEvent): void {
        if (!this.isDragging) return;

        const deltaX = event.clientX - this.lastX;
        const sensitivity = 2; // Adjust for sensitivity

        if (Math.abs(deltaX) > sensitivity) {
            const frameChange = Math.floor(deltaX / sensitivity);
            this.currentFrame = (this.currentFrame + frameChange + this.totalFrames) % this.totalFrames;
            this.lastX = event.clientX;
        }

        event.preventDefault();
    }

    /**
     * Stop drag interaction
     */
    stopDrag(): void {
        this.isDragging = false;
    }

    /**
     * Start touch interaction
     */
    startTouch(event: TouchEvent): void {
        this.isDragging = true;
        this.lastX = event.touches[0].clientX;
        this.stopAutoRotate();
        event.preventDefault();
    }

    /**
     * Handle touch movement
     */
    onTouch(event: TouchEvent): void {
        if (!this.isDragging) return;

        const deltaX = event.touches[0].clientX - this.lastX;
        const sensitivity = 2;

        if (Math.abs(deltaX) > sensitivity) {
            const frameChange = Math.floor(deltaX / sensitivity);
            this.currentFrame = (this.currentFrame + frameChange + this.totalFrames) % this.totalFrames;
            this.lastX = event.touches[0].clientX;
        }

        event.preventDefault();
    }

    /**
     * Stop touch interaction
     */
    stopTouch(): void {
        this.isDragging = false;
    }

    /**
     * Toggle auto rotation
     */
    toggleAutoRotate(): void {
        if (this.autoRotate) {
            this.stopAutoRotate();
        } else {
            this.startAutoRotate();
        }
    }

    /**
     * Start auto rotation
     */
    private startAutoRotate(): void {
        this.autoRotate = true;
        this.autoRotateInterval = setInterval(() => {
            this.currentFrame = (this.currentFrame + 1) % this.totalFrames;
        }, 100); // Rotate every 100ms
    }

    /**
     * Stop auto rotation
     */
    private stopAutoRotate(): void {
        this.autoRotate = false;
        if (this.autoRotateInterval) {
            clearInterval(this.autoRotateInterval);
            this.autoRotateInterval = null;
        }
    }

    /**
     * Reset view to starting position
     */
    resetView(): void {
        this.currentFrame = 0;
        this.stopAutoRotate();
    }
}
